import { useState, useRef, useEffect } from 'react';
import { Client } from '@stomp/stompjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/common/Button';
import { 
  Wand2, 
  FileText, 
  Trash2, 
  X, 
  Sparkles, 
  Download, 
  CheckCircle2, 
  Circle, 
  ChevronRight,
  Info,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Plus,
  Loader2,
  Send,
  Copy,
  Check,
  Layout,
  User
} from 'lucide-react';
import axiosInstance from '@/services/axiosInstance';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import './styles.scss';
import AdvisingDashboard from './AdvisingDashboard';
import { formatTimestamp, formatTime } from '@/utils/dateUtils';


const AdvisingDiagnostic = ({ institution, displayName, pdfColorRgb }) => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [question, setQuestion] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [executionSteps, setExecutionSteps] = useState([]);
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [showQuickQueries, setShowQuickQueries] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const placeholders = [
    "How can I align my current IT credits with a career in Cyber Security?",
    "How much will the BAS degree cost and are there scholarships?",
    "What specific credits will transfer from my JSCC CITC program?",
    "What's the difference between BAS-Manufacturing and BAS-Industrial Technology?",
    "Show me the median salary for Cloud Architects in the Memphis area.",
    "Will my COMP 1263 course at JSCC count toward my BAS degree?",
    "Which BAS concentrations are currently in highest demand by employers?",
    "How many general education requirements do I have left to complete?",
    "Can you create a 2-year roadmap for completing my BAS degree?",
    "What are the entry-level roles I can apply for after graduating?",
    "Are there financial aid options specifically for adult learners returning to school?",
    "What internship opportunities are available for BAS-Supply Chain students?",
    "Can I complete the BAS-Project Management concentration fully online?",
    "What are the evening and weekend course options for working professionals?",
    "How does the BAS degree align with CompTIA Security+ or AWS certifications?",
    "Is there an accelerated pathway for students with 10+ years of work experience?",
    "What is the expected ROI if I transfer from a 2-year Computer Programming degree?",
    "Which concentrations offer the highest growth potential in the next 5 years?",
    "How do I petition for Prior Learning Assessment (PLA) credits for work experience?",
    "What are the capstone project requirements for the BAS degree?"
  ];

  const quickQueries = [
    {
      category: "Career & Alignment",
      queries: [
        "How can I align my current IT credits with a career in Cyber Security?",
        "What are the entry-level roles I can apply for after graduating?",
        "How do I petition for Prior Learning Assessment (PLA) credits for work experience?",
        "How does the BAS degree align with CompTIA Security+ or AWS certifications?"
      ]
    },
    {
      category: "Cost & Financials",
      queries: [
        "How much will the BAS degree cost and are there scholarships?",
        "Are there financial aid options specifically for adult learners returning to school?",
        "What is the expected ROI if I transfer from a 2-year Computer Programming degree?"
      ]
    },
    {
      category: "Credit Transfer",
      queries: [
        "What specific credits will transfer from my JSCC CITC program?",
        "Will my COMP 1263 course at JSCC count toward my BAS degree?",
        "How many general education requirements do I have left to complete?"
      ]
    },
    {
      category: "Program Choice",
      queries: [
        "What's the difference between BAS-Manufacturing and BAS-Industrial Technology?",
        "Which BAS concentrations are currently in highest demand by employers?",
        "Can I complete the BAS-Project Management concentration fully online?",
        "What are the evening and weekend course options for working professionals?"
      ]
    },
    {
      category: "Workforce & Roadmap",
      queries: [
        "Show me the median salary for Cloud Architects in the Memphis area.",
        "Can you create a 2-year roadmap for completing my BAS degree?",
        "What internship opportunities are available for BAS-Supply Chain students?",
        "Which concentrations offer the highest growth potential in the next 5 years?",
        "What are the capstone project requirements for the BAS degree?"
      ]
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholders.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [placeholders.length]);
  const currentExecutionIdRef = useRef(null);
  const resultsEndRef = useRef(null);
  const stompClientRef = useRef(null);
  const fileInputRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const lastValidReport = useRef('');

  const scrollToBottom = () => {
    resultsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Auto-scroll whenever streaming content or progress steps update
  useEffect(() => {
    if (loading || (result && result.status === 'STREAMING')) {
      scrollToBottom();
    }
  }, [loading, result, executionSteps]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setExecutionSteps([]);

    const formData = new FormData();
    formData.append('question', question);
    formData.append('institution', institution);
    if (file) {
      formData.append('transcriptFile', file);
    }

    try {
      const response = await axiosInstance.post('/api/advising/diagnose', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const data = response.data;
      const executionId = data.executionId;

      // Check if we already have a final result in the response (Master Report style)
      const output = data?.outputData || data?.response || data;
      let linqResult = output?.result || output?.finalResult || output;
      const hasImmediateResult = (typeof linqResult === 'string' && (linqResult.includes('#') || linqResult.includes('|'))) ||
        (data.final_report) || (data.finalReport);

      if (executionId && !hasImmediateResult) {
        setCurrentExecutionId(executionId);
        currentExecutionIdRef.current = executionId;
        setExecutionSteps([{
          status: 'INITIATED',
          message: 'Initializing AI Advisory Engine...',
          timestamp: new Date().toISOString()
        }]);
        connectWebSocket(executionId);
      } else {
        setResult(data);
        setLoading(false);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError("The AI service is currently busy (quota limit reached). Please wait a minute and try again.");
      } else {
        setError(err.response?.data?.message || err.message);
      }
      setLoading(false);
    }
  };

  const connectWebSocket = (executionId) => {
    if (stompClientRef.current) {
      stompClientRef.current.deactivate();
    }

    // Dynamically determine Gateway WebSocket URL from environment or current host
    const gatewayUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://localhost:7777/r/polytechnic-app';

    // Extract base URL (protocol + host) for WebSocket connection
    let gatewayWsUrl;
    try {
      // Check if it's a relative path (starts with /)
      if (gatewayUrl.startsWith('/')) {
        const origin = window.location.origin;
        gatewayWsUrl = `${origin.replace(/^http/, 'ws')}/ws-linqra`;
      } else {
        const url = new URL(gatewayUrl);
        // The WebSocket endpoint is at the root of the gateway, not under the app prefix
        gatewayWsUrl = `${url.protocol.replace('http', 'ws')}//${url.host}/ws-linqra`;
      }
    } catch (e) {
      console.warn('Failed to parse VITE_API_GATEWAY_URL, falling back to current host:', e);
      gatewayWsUrl = `${window.location.protocol.replace('http', 'ws')}//${window.location.host}/ws-linqra`;
    }

    // Force wss if using port 7777 (Gateway) or if protocol is already https
    let brokerUrl = gatewayWsUrl.replace(/^http/, 'ws');
    if (gatewayWsUrl.includes(':7777') && !brokerUrl.startsWith('wss')) {
      brokerUrl = brokerUrl.replace('ws://', 'wss://');
    }
    console.log('Connecting to Gateway WebSocket (Native):', brokerUrl);

    const client = new Client({
      brokerURL: brokerUrl,
      debug: (str) => console.log('STOMP DEBUG:', str),
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
    });

    client.onConnect = (frame) => {
      console.log('Connected to Linqra WebSocket:', frame);

      // Subscribe to execution updates
      client.subscribe('/topic/execution', (message) => {
        try {
          const messageId = message.headers['message-id'];
          if (messageId && processedMessageIds.current.has(messageId)) {
            console.log('Skipping duplicate message:', messageId);
            return;
          }
          if (messageId) {
            processedMessageIds.current.add(messageId);
          }

          const update = JSON.parse(message.body);
          console.log('Received execution update:', update);

          if (update.executionId === executionId) {
            handleExecutionUpdate(update);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      });
    };

    client.onStompError = (frame) => {
      console.error('STOMP error:', frame.headers['message']);
      console.error('STOMP details:', frame.body);
      // Fallback to polling if WebSocket fails
      pollExecutionStatus(executionId);
    };

    client.activate();
    stompClientRef.current = client;
  };

  const handleExecutionUpdate = (update) => {
    // Prevent updates from cancelled or stale executions
    if (!currentExecutionIdRef.current || update.executionId !== currentExecutionIdRef.current) {
      console.log('Ignoring update for stale/cancelled execution:', update.executionId);
      return;
    }

    const { status, message, result, progress, currentStepName, chunk } = update;

    if (status === 'STREAMING') {
      setResult(prev => {
        // Initialize if first chunk or different execution
        if (!prev || prev.executionId !== update.executionId) {
          return {
            ...update,
            finalResult: update.accumulated || chunk || ''
          };
        }

        // Use accumulated if available (prevent doubling), otherwise fallback to appending chunk
        const newFinalResult = update.accumulated !== undefined && update.accumulated !== null
          ? update.accumulated
          : (prev.finalResult || '') + (chunk || '');

        return {
          ...prev,
          ...update,
          finalResult: newFinalResult,
          status: 'STREAMING',
          // Ensure request is preserved during streaming
          request: update.request || update.input_data || prev?.request || {
            question: question,
            institution: institution
          }
        };
      });
      return;
    }

    setExecutionSteps(prev => {
      const newStep = {
        status,
        message: currentStepName || message || `Processing step...`,
        timestamp: new Date().toISOString(),
        progress
      };

      // Deduplicate: if a step with the same status+message already exists, skip it.
      // For RUNNING steps, replace the existing entry with the latest update instead of appending.
      const existingIndex = prev.findIndex(s => s.status === status && s.message === newStep.message);
      if (existingIndex !== -1) {
        if (status === 'RUNNING') {
          // Replace in-place to update progress/timestamp without duplicating
          const updated = [...prev];
          updated[existingIndex] = newStep;
          return updated;
        }
        return prev;
      }

      return [...prev, newStep];
    });

    if (status === 'COMPLETED' || status === 'SUCCESS') {
      setResult(prev => ({
        ...update,
        // Preserve input context from component state if missing in update
        request: update.request || update.input_data || prev?.request || {
          question: question,
          institution: institution
        }
      }));
      setLoading(false);
      // Clear form after success
      setQuestion('');
      removeFile();
      if (stompClientRef.current) stompClientRef.current.deactivate();
    } else if (status === 'FAILED') {
      // If the error message indicates cancellation, treat it as such
      if (update.errorMessage?.toLowerCase().includes('cancel')) {
        setResult(null);
        setExecutionSteps([]);
        setError('The analysis was stopped.');
      } else {
        setError(update.errorMessage || 'AI Advisor execution failed.');
      }
      setLoading(false);
      if (stompClientRef.current) stompClientRef.current.deactivate();
    } else if (status === 'CANCELLED') {
      setResult(null);
      setExecutionSteps([]);
      setError('The analysis was stopped.');
      setLoading(false);
      if (stompClientRef.current) stompClientRef.current.deactivate();
    }
  };

  const handleCancel = async () => {
    if (!currentExecutionId) return;

    try {
      await axiosInstance.post(`/api/advising/cancel/${currentExecutionId}`);
      currentExecutionIdRef.current = null;
      setCurrentExecutionId(null);
      setResult(null);
      setExecutionSteps([]);
      setError('The analysis was stopped.');
      setLoading(false);
      if (stompClientRef.current) stompClientRef.current.deactivate();
    } catch (err) {
      console.error('Failed to cancel execution:', err);
      // Fallback: just stop UI loading state
      setLoading(false);
    }
  };

  const pollExecutionStatus = async (executionId) => {
    const pollInterval = 3000;
    let attempts = 0;
    const maxAttempts = 40;

    const poll = async () => {
      try {
        const response = await axiosInstance.get(`/api/advising/status/${executionId}`);
        const data = response.data;

        handleExecutionUpdate(data);

        if (data.status !== 'COMPLETED' && data.status !== 'SUCCESS' && data.status !== 'FAILED' && attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, pollInterval);
        }
      } catch (err) {
        console.error('Polling failed:', err);
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, pollInterval);
        } else {
          setError('Connection lost while waiting for results.');
          setLoading(false);
        }
      }
    };

    setTimeout(poll, pollInterval);
  };

  useEffect(() => {
    return () => {
      if (stompClientRef.current) {
        stompClientRef.current.deactivate();
      }
    };
  }, []);

  const handleCopy = (text) => {
    if (!text) return;
    const cleanText = stripMarkdown(text);
    navigator.clipboard.writeText(cleanText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stripMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text;
    try {
      // Ensure synchronous parsing for utility functions
      const html = marked.parse(text);
      if (typeof html !== 'string') return text.replace(/[#*`_~]/g, '');

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      return doc.body.innerText || doc.body.textContent || "";
    } catch (err) {
      console.error("Markdown stripping failed:", err);
      return text.replace(/[#*`_~]/g, '');
    }
  };

  const handleExportPDF = (executionData) => {
    try {
      const doc = new jsPDF();

      // Use the same search and parse logic as the main renderer
      const output = executionData?.outputData || executionData?.response || executionData;
      let linqResult = output?.result || output?.finalResult || output;

      const tryParseLocal = (val) => {
        if (typeof val === 'object' && val !== null) return val;
        if (typeof val !== 'string') return null;
        const trimmed = val.trim();
        if (trimmed.includes('{') && trimmed.includes('}')) {
          try {
            const startIdx = trimmed.indexOf('{');
            const endIdx = trimmed.lastIndexOf('}') + 1;
            return JSON.parse(trimmed.substring(startIdx, endIdx));
          } catch (e) { return null; }
        }
        return null;
      };

      let parsed = tryParseLocal(linqResult);
      const dashboardData = findDeep(parsed || linqResult, 'dashboard_data') || findDeep(parsed || linqResult, 'dashboardData');
      const masterReport = findDeep(parsed || linqResult, 'final_report') || findDeep(parsed || linqResult, 'finalReport');
      const isMasterReport = !!masterReport;

      const fallbackResult = !isMasterReport ? linqResult : null;

      // 1. Header
      let r = 237, g = 117, b = 52;
      if (pdfColorRgb) {
        const parts = pdfColorRgb.split(',').map(n => parseInt(n.trim(), 10));
        if (parts.length === 3 && !parts.some(isNaN)) {
          [r, g, b] = parts;
        }
      }
      doc.setFillColor(r, g, b);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      const isValidName = (name) => {
        if (!name || typeof name !== 'string') return false;
        const n = name.trim();
        // Names shouldn't be too long or contain common markdown/report symbols
        return n.length > 1 && n.length < 60 && !n.includes('|') && !n.includes('#') && !n.includes('{');
      };

      let studentName = findDeep(parsed || linqResult, 'student_name') ||
        findDeep(parsed || linqResult, 'studentName') ||
        executionData?.inputData?.fullName ||
        executionData?.inputData?.name ||
        executionData?.request?.fullName ||
        executionData?.request?.name;

      if (!isValidName(studentName)) {
        studentName = null;
      }

      // Fallback: Scan transcript for "Student Name: [Name]"
      if (!studentName && executionData?.inputData?.transcript) {
        const transcriptText = executionData.inputData.transcript;
        const nameMatch = transcriptText.match(/(?:Student Name|Name|STUDENT):\s*([^\n\r*]+)/i);
        if (nameMatch && isValidName(nameMatch[1])) {
          studentName = nameMatch[1].trim();
        }
      }

      const finalAdvice = masterReport || (typeof linqResult === 'string' ? linqResult : JSON.stringify(linqResult, null, 2));

      doc.text(studentName ? `${studentName}'s AI Advisory Report` : "AI Advisory Report", 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${formatTimestamp(new Date())}`, 20, 30);

      // 2. Metadata / Context
      let y = 50;
      doc.setTextColor(0, 0, 0);

      // Ensure question is always included
      const questionToUse = executionData?.request?.question || 
                       executionData?.inputData?.question || 
                       executionData?.input_data?.question ||
                       executionData?.request?.query?.question ||
                       executionData?.request?.query?.params?.question ||
                       question || // From state
                       "AI Advisory Consultation";
                       
      const transcriptToUse = executionData?.request?.transcript || 
                         executionData?.inputData?.transcript || 
                         executionData?.input_data?.transcript ||
                         executionData?.request?.query?.transcript ||
                         executionData?.request?.query?.params?.transcript;
                         // Note: we don't have transcript text in state easily here

      if (questionToUse) {
        doc.setFontSize(10);
        doc.setTextColor(237, 117, 52);
        doc.setFont(undefined, 'bold');
        doc.text("STUDENT INQUIRY:", 20, y);
        y += 6;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        const lines = doc.splitTextToSize(questionToUse, 170);
        doc.text(lines, 20, y);
        y += (lines.length * 5) + 10;
      }

      if (transcriptToUse && transcriptToUse !== 'No transcript provided. Use institution assumptions.') {
        doc.setFontSize(10);
        doc.setTextColor(237, 117, 52);
        doc.setFont(undefined, 'bold');
        doc.text("SOURCE CONTEXT:", 20, y);
        y += 6;
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'normal');
        const transcriptPreview = transcriptToUse.length > 500 ? transcriptToUse.substring(0, 500) + "..." : transcriptToUse;
        const lines = doc.splitTextToSize(transcriptPreview, 170);
        doc.text(lines, 20, y);
        y += (lines.length * 5) + 10;
      }
      
      y += 5; // Extra padding before main content

      const renderSection = (title, content) => {
        if (!content) return;

        const text = typeof content === 'string' ? content : objectToMarkdown(content);
        if (text.length < 5) return;

        // Title
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(title.toUpperCase(), 20, y);
        y += 12;

        // Split content into blocks (tables vs text)
        const blocks = text.split(/(\|[^\n]+\|\n\|[\s|:\-]+\|\n(?:\|[^\n]+\|\n?)+)/g);

        blocks.forEach(block => {
          if (!block.trim()) return;

          // Check if this block is a table
          if (block.trim().startsWith('|') && block.includes('---')) {
            const rows = block.trim().split('\n').filter(r => r.includes('|') && !r.includes('---'));
            if (rows.length > 0) {
              const head = [rows[0].split('|').filter(c => c.trim()).map(c => c.trim())];
              const body = rows.slice(1).map(r => r.split('|').filter(c => c.trim()).map(c => c.trim()));

              autoTable(doc, {
                head: head,
                body: body,
                startY: y,
                margin: { left: 20, right: 20 },
                theme: 'striped',
                headStyles: { fillColor: [237, 117, 52] },
                styles: { fontSize: 9 },
                didDrawPage: (data) => {
                  y = data.cursor.y + 10;
                }
              });
              y = doc.lastAutoTable.finalY + 12;
            }
          } else {
            // Split block into lines to detect headers
            const lines = block.split('\n');
            lines.forEach(line => {
              if (!line.trim()) {
                y += 5;
                return;
              }

              // Detect headers
              const headerMatch = line.match(/^(#{1,6})\s+(.*)/);
              if (headerMatch) {
                const level = headerMatch[1].length;
                const textContent = headerMatch[2];

                // Style for headers
                doc.setFont("helvetica", "bold");
                doc.setFontSize(level === 1 ? 16 : level === 2 ? 14 : 12);
                doc.setTextColor(0, 0, 0);

                if (y > 270) { doc.addPage(); y = 20; }
                doc.text(textContent, 20, y);
                y += 8;

                // Reset styles
                doc.setFont("helvetica", "normal");
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
              } else {
                // Regular text line with robust inline bold support
                let remaining = line;
                let curX = 20;

                // Pre-check for page overflow
                if (y > 280) { doc.addPage(); y = 20; }

                // Parse line into segments
                const segments = [];
                while (remaining) {
                  const boldMatch = remaining.match(/^(\*\*(.*?)\*\*)/);
                  if (boldMatch) {
                    segments.push({ text: boldMatch[2], bold: true });
                    remaining = remaining.substring(boldMatch[0].length);
                  } else {
                    const nextBold = remaining.indexOf('**');
                    if (nextBold === -1) {
                      segments.push({ text: remaining, bold: false });
                      remaining = '';
                    } else {
                      segments.push({ text: remaining.substring(0, nextBold), bold: false });
                      remaining = remaining.substring(nextBold);
                    }
                  }
                }

                // Draw segments with manual wrapping
                segments.forEach(seg => {
                  doc.setFont("helvetica", seg.bold ? "bold" : "normal");
                  // Clean segment text of other markdown (italics, code blocks)
                  const cleanSegText = stripMarkdown(seg.text);
                  const words = cleanSegText.split(/(\s+)/);

                  words.forEach(word => {
                    if (!word) return;
                    const wordWidth = doc.getTextWidth(word);

                    // Wrap if we exceed the margin
                    if (curX + wordWidth > 195) {
                      curX = 20;
                      y += 7;
                      if (y > 280) { doc.addPage(); y = 20; }
                    }

                    doc.text(word, curX, y);
                    curX += wordWidth;
                  });
                });

                y += 7; // Line spacing after paragraph line
              }
            });
            y += 5;
          }
        });

        y += 5;
      };

      if (dashboardData) {
        // Dashboard Stats Table
        const stats = dashboardData.summary || {};
        autoTable(doc, {
          head: [['Metric', 'Value']],
          body: [
            ['Transfer Efficiency', stats.transferEfficiency || 'N/A'],
            ['Credits to Transfer', String(stats.creditsTransfer || 'N/A')],
            ['Time to BAS Degree', stats.timeToBAS || 'N/A'],
            ['Average Market Salary', stats.avgSalary || 'N/A']
          ],
          startY: y,
          margin: { left: 20, right: 20 },
          theme: 'grid',
          headStyles: { fillColor: [237, 117, 52] },
          styles: { fontSize: 10 }
        });
        y = doc.lastAutoTable.finalY + 15;

        // Job Market Section
        if (dashboardData.jobMarket && dashboardData.jobMarket.jobs) {
          doc.setFontSize(14);
          doc.setTextColor(0, 0, 0);
          doc.text("REGIONAL JOB MARKET - " + (dashboardData.jobMarket.location || "MEMPHIS, TN"), 20, y);
          y += 10;

          autoTable(doc, {
            head: [['Title', 'Company', 'Salary', 'Growth']],
            body: dashboardData.jobMarket.jobs.map(j => [j.title, j.company, j.salary, j.growth]),
            startY: y,
            margin: { left: 20, right: 20 },
            theme: 'striped',
            headStyles: { fillColor: [237, 117, 52] },
            styles: { fontSize: 9 }
          });
          y = doc.lastAutoTable.finalY + 15;
        }
      }

      if (isMasterReport) {
        renderSection("24-Month Strategic Advisory Report", masterReport);
      } else {
        renderSection("Strategic Pathway Analysis", fallbackResult);
      }

      doc.save(`Pathway_Analysis_${Date.now()}.pdf`);
    } catch (err) {
      console.error("PDF Export failed:", err);
      alert("PDF generation failed. Please use the Copy button as fallback.");
    }
  };

  const findDeep = (obj, targetKey) => {
    if (!obj) return null;
    
    // If it's a string, check if it contains a JSON object
    if (typeof obj === 'string') {
      const trimmed = obj.trim();
      if (trimmed.includes('{') && trimmed.includes('}')) {
        try {
          const startIdx = trimmed.indexOf('{');
          const endIdx = trimmed.lastIndexOf('}') + 1;
          const parsed = JSON.parse(trimmed.substring(startIdx, endIdx));
          return findDeep(parsed, targetKey);
        } catch (e) { /* ignore and continue */ }
      }
      return null;
    }
    
    if (typeof obj !== 'object') return null;
    if (obj[targetKey]) return obj[targetKey];
    
    for (const key in obj) {
      const found = findDeep(obj[key], targetKey);
      if (found) return found;
    }
    return null;
  };

  const renderDiagnosticResults = (executionData) => {
    // Navigate the AgentExecution -> LinqResponse -> result structure
    const output = executionData?.outputData || executionData?.response || executionData;

    // Try to find the result at various levels
    let linqResult = output?.finalResult || output?.result || output;

    // During streaming, we might have injected finalResult directly into the state
    if (executionData.status === 'STREAMING' && executionData.finalResult) {
      linqResult = executionData.finalResult;
    }

    const tryParse = (val) => {
      if (typeof val === 'object' && val !== null) return val;
      if (typeof val !== 'string') return null;
      const trimmed = val.trim();

      // 1. Try full JSON parse by extracting the bracketed object
      if (trimmed.includes('{') && trimmed.includes('}')) {
        try {
          const startIdx = trimmed.indexOf('{');
          const endIdx = trimmed.lastIndexOf('}') + 1;
          return JSON.parse(trimmed.substring(startIdx, endIdx));
        } catch (e) { /* fall through to extraction */ }
      }

      // 2. Extract content from "final_report" and "student_name" during streaming
      // Robust regex that handles escaped characters and partial streams
      const reportMatch = trimmed.match(/"final(?:_r|R)eport"\s*:\s*"((?:[^"\\]|\\.)*)/);
      const nameMatch = trimmed.match(/"student(?:_n|N)ame"\s*:\s*"((?:[^"\\]|\\.)*)/);

      if (reportMatch) {
        // Unescape common JSON characters
        const content = reportMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"');
        const studentName = nameMatch ? nameMatch[1].replace(/\\n/g, '\n').replace(/\\"/g, '"') : null;

        return {
          final_report: content,
          student_name: studentName
        };
      }

      // 3. Fallback: If we see dashboard_data but not final_report yet
      if (trimmed.includes('"dashboard_data"') || trimmed.includes('"dashboardData"')) {
        try {
          // Try to extract a partial dashboard_data object if it's small enough or finished
          const dashMatch = trimmed.match(/"dashboard(?:_d|D)ata"\s*:\s*(\{.*?\})/s);
          if (dashMatch) {
            return { dashboard_data: JSON.parse(dashMatch[1]) };
          }
        } catch (e) { /* ignore */ }
      }

      return null;
    };

    let parsed = tryParse(linqResult);

    // Check for premium dashboard data
    const dashboardData = findDeep(parsed || linqResult, 'dashboard_data') || findDeep(parsed || linqResult, 'dashboardData');

    // Find the master report
    const masterReport = findDeep(parsed || linqResult, 'final_report') || findDeep(parsed || linqResult, 'finalReport');
    const isMasterReport = !!masterReport;

    // Detect if we are looking at a raw JSON structure that hasn't been parsed yet
    const isRawJson = typeof linqResult === 'string' &&
      (linqResult.trim().startsWith('{') || linqResult.trim().includes('"final_report"')) &&
      !parsed;

    // Fallback for single-string results
    // If it's raw JSON and we haven't parsed it yet, show a placeholder instead of the braces
    let finalAdvice = masterReport || (typeof linqResult === 'string' ? linqResult : JSON.stringify(linqResult, null, 2));

    // Sticky content logic: If we already have a previous result that was parsed, and the current one isn't, 
    // we keep the old one during streaming to avoid flickering back to the placeholder.
    if (masterReport) {
      lastValidReport.current = masterReport;
    }

    if (isRawJson && executionData.status === 'STREAMING') {
      // If we have a sticky report, use it instead of the placeholder
      finalAdvice = lastValidReport.current || "AI Advisor is preparing your report...";
    }

    // Check for student name if available
    const isValidName = (name) => {
      if (!name || typeof name !== 'string') return false;
      const n = name.trim();
      return n.length > 1 && n.length < 60 && !n.includes('|') && !n.includes('#') && !n.includes('{');
    };

    let studentName = findDeep(parsed || linqResult, 'student_name') ||
      findDeep(parsed || linqResult, 'studentName') ||
      executionData?.inputData?.fullName ||
      executionData?.inputData?.name ||
      executionData?.request?.fullName ||
      executionData?.request?.name;

    if (!isValidName(studentName)) {
      studentName = null;
    }

    if (dashboardData) {
      return (
        <div className="diagnostic-results dashboard-mode">
          <div className="results-header">
            <div className="header-title">
              <Layout className="icon-sparkle" size={20} />
              <h3>{studentName ? `${studentName}'s Intelligence Dashboard` : 'Intelligence Dashboard'}</h3>
            </div>
            <div className="header-actions">
              <button
                className="action-btn"
                onClick={() => handleCopy(finalAdvice)}
                title="Copy Summary"
              >
                {copied ? <Check size={18} color="#10b981" /> : <Copy size={18} />}
                <span>Copy Summary</span>
              </button>
              <button
                className="action-btn"
                onClick={() => handleExportPDF(executionData)}
                title="Download Report"
              >
                <Download size={18} />
                <span>Export PDF</span>
              </button>
            </div>
          </div>
          <AdvisingDashboard 
            data={parsed || linqResult} 
            question={executionData.request?.question || executionData.input_data?.question}
            transcript={executionData.request?.transcript || executionData.input_data?.transcript}
          />
        </div>
      );
    }

    return (
      <div className="diagnostic-results">
        <div className="results-header">
          <div className="header-title">
            <Sparkles className="icon-sparkle" size={20} />
            <h3>{studentName ? `${studentName}'s Advisory Report` : 'AI Advisory Report'}</h3>
          </div>
          <div className="header-actions">
            <button
              className="action-btn"
              onClick={() => handleCopy(finalAdvice)}
              title="Copy to Clipboard"
            >
              {copied ? <Check size={18} color="#10b981" /> : <Copy size={18} />}
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button
              className="action-btn"
              onClick={() => handleExportPDF(executionData)}
              title="Download PDF"
            >
              <Download size={18} />
              <span>Export PDF</span>
            </button>
          </div>
        </div>

        <div className="results-rows">
          {studentName && (
            <div className="result-row student-profile-row">
              <div className="row-title">
                <User size={16} />
                <h4>Student Profile</h4>
              </div>
              <div className="row-content">
                <div className="profile-badge">
                  <div className="profile-avatar">
                    {studentName.charAt(0).toUpperCase()}
                  </div>
                  <div className="profile-info">
                    <span className="profile-label">Full Name</span>
                    <span className="profile-value">{studentName}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="result-row master-report">
            <div className="row-title">
              <Sparkles size={16} />
              <h4>{isMasterReport ? 'Strategic Summary' : 'Advisory Summary'}</h4>
            </div>
            <div className="row-content">
              {formatAdvice(finalAdvice)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProgress = () => {
    if (!loading || !currentExecutionId) return null;

    const latestStep = executionSteps[executionSteps.length - 1];
    const progress = latestStep?.progress || (executionSteps.length / 5) * 100;

    return (
      <div className="diagnostic-progress-container">
        <div className="progress-header">
          <div className="status-badge animated-pulse">
            <Loader2 size={14} className="animate-spin" />
            <span>AI ADVISOR ANALYZING</span>
          </div>
          <div className="header-actions-inline">
            <span className="execution-id">Task ID: {currentExecutionId.substring(0, 8)}...</span>
            <button
              className="btn-cancel-diagnostic"
              onClick={handleCancel}
              type="button"
            >
              <X size={14} />
              <span>Cancel</span>
            </button>
          </div>
        </div>

        <div className="progress-bar-wrapper">
          <div className="progress-bar-fill" style={{ width: `${Math.min(progress, 100)}%` }} />
        </div>

        <div className="execution-steps">
          {executionSteps.map((step, index) => (
            <div key={index} className={`step-item ${index === executionSteps.length - 1 ? 'current' : 'completed'}`}>
              <div className="step-icon">
                {index === executionSteps.length - 1 ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Check size={14} />
                )}
              </div>
              <div className="step-content">
                <p className="step-message">{step.message}</p>
                <span className="step-time">{formatTime(new Date(step.timestamp))}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const objectToMarkdown = (obj, level = 0) => {
    if (obj === null || obj === undefined) return '';
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);

    if (Array.isArray(obj)) {
      return obj.map(item => `- ${objectToMarkdown(item, level + 1)}`).join('\n');
    }

    if (typeof obj === 'object') {
      return Object.entries(obj).map(([key, value]) => {
        const cleanKey = key.replace(/_/g, ' ');
        const headerLevel = Math.min(level + 3, 6);
        const header = '#'.repeat(headerLevel);

        if (typeof value === 'object' && value !== null) {
          return `${header} ${cleanKey}\n${objectToMarkdown(value, level + 1)}`;
        }
        return `**${cleanKey}**: ${value}`;
      }).join('\n\n');
    }

    return String(obj);
  };

  const formatAdvice = (content) => {
    if (!content) return null;

    // Convert object/array to Markdown string if necessary
    let markdownText = typeof content === 'string' ? content : objectToMarkdown(content);

    // Strip redundant top-level header (# AI Advisory Report etc) to prevent duplication with UI header
    if (markdownText.trim().startsWith('# ')) {
      markdownText = markdownText.replace(/^#\s+.*?(?:\r?\n|$)/, '');
    }

    try {
      const html = marked.parse(markdownText);
      const cleanHtml = DOMPurify.sanitize(html);

      // Wrap tables in a scrollable div for better mobile/narrow layout support
      const wrappedHtml = cleanHtml.replace(/<table>/g, '<div class="table-wrapper"><table>')
        .replace(/<\/table>/g, '</table></div>');

      return <div className="markdown-content" dangerouslySetInnerHTML={{ __html: wrappedHtml }} />;
    } catch (err) {
      console.error("Markdown parsing failed:", err);
      return <div className="markdown-content">{String(markdownText)}</div>;
    }
  };

  return (
    <div className="advising-diagnostic-container">
      <div className="diagnostic-card">
        <div className="diagnostic-header">
          <div className="header-icon">
            <Sparkles size={24} />
          </div>
          <div className="header-text">
            <h2>AI Advisor for your Academic Career</h2>
            {/* <p>High-fidelity pathway analysis using academic and workforce intelligence</p> */}
          </div>
          <div className="header-actions">
            <div className="session-selector-badge static-mode">
              <span className="badge-label">TARGET INSTITUTION:</span>
              <span className="institution-static-name">
                {displayName || institution}
              </span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="diagnostic-form">
          <div className="input-area">
            <div className="quick-queries-container">
              <button 
                type="button" 
                className={`btn-quick-queries ${showQuickQueries ? 'active' : ''}`}
                onClick={() => setShowQuickQueries(!showQuickQueries)}
              >
                <MessageSquare size={16} />
                <span>Quick Queries</span>
                {showQuickQueries ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>

              {showQuickQueries && (
                <div className="quick-queries-dropdown">
                  <div className="dropdown-header">
                    <h4>Suggested Pathway Questions</h4>
                    <button type="button" onClick={() => setShowQuickQueries(false)} className="close-dropdown">
                      <X size={16} />
                    </button>
                  </div>
                  <div className="dropdown-content">
                    {quickQueries.map((group, idx) => (
                      <div key={idx} className="query-group">
                        <h5>{group.category}</h5>
                        <div className="query-list">
                          {group.queries.map((q, qidx) => (
                            <button
                              key={qidx}
                              type="button"
                              className="query-item"
                              onClick={() => {
                                setQuestion(q);
                                setShowQuickQueries(false);
                              }}
                            >
                              <ChevronRight size={14} />
                              <span>{q}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <textarea
              placeholder={placeholders[placeholderIndex]}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              disabled={loading}
              rows={4}
              className="rotating-placeholder"
            />

            <div className="form-actions">
              <div className="file-upload-wrapper">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.txt,.docx,.xlsx"
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className={`btn-file-upload ${file ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  title="Upload Transcript (PDF, TXT, DOCX, XLSX)"
                >
                  {file ? <FileText size={18} /> : <Plus size={18} />}
                  <span>{file ? file.name : 'Attach Transcript'}</span>
                  {file && (
                    <X
                      size={14}
                      className="remove-file-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile();
                      }}
                    />
                  )}
                </button>
              </div>

              <Button
                type="submit"
                variant="primary"
                disabled={loading || !question.trim()}
                className="btn-diagnose"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Consult AI Advisor
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>

        {error && (
          <div className="diagnostic-error">
            <p>{error}</p>
          </div>
        )}

        {(loading || result) && (
          <div className="inquiry-label">
            <Send className="label-icon" size={14} />
            <span>YOUR INQUIRY:</span>
            <span className="label-text">"{result?.request?.question || result?.input_data?.question || question}"</span>
          </div>
        )}

        {loading && renderProgress()}

        {result && (result.status === 'STREAMING' || !loading) && renderDiagnosticResults(result)}
        <div ref={resultsEndRef} style={{ height: '1px' }} />

        <div className="diagnostic-footer">
          <span className="footer-link">
            To have more detailed information about the market, visit <a href="https://smartadvising.ai" target="_blank" rel="noopener noreferrer">smartadvising.ai</a>
          </span>
          <span className="powered-by">POWERED BY <span className="brand">LINQRA</span></span>
        </div>
      </div>
    </div>
  );
};

export default AdvisingDiagnostic;
